import React, { memo, useRef } from "react";
import { formats } from "investira.sdk";
import {
  Stack,
  Typography,
  Chip,
  Icon,
  IconButton,
  Checkbox,
  Tooltip,
} from "investira.react.components";
import { displays } from "investira.react.lib";
import { ATIVO_MERCADO } from "@investirapri/financa.js/lib/enums";
import { PessoaImage } from "../../components/molecules";

const BenchmarkListItem = memo((props) => {
  const {
    benchmark = {},
    variant = "default",
    checked,
    onClick,
    onChange,
    metadata = {},
  } = props;

  const INDICADORES_TIPO = useRef({
    M: {
      label: "Moeda",
      id: "M",
      icon: "coin",
    },
    C: {
      label: "Correção Monetária",
      id: "C",
      icon: "coins",
    },
    I: {
      label: "Índice de Mercado",
      id: "I",
      icon: "chart_line",
    },
    J: {
      label: "Juros",
      id: "J",
      icon: "percent",
    },
    T: { label: "Metal", id: "T", icon: "metal" },
  });

  const xIndicadoresTipo = INDICADORES_TIPO.current;

  const renderAtivo = (pData) => {
    const {
      ticker,
      ativo_seuid,
      ativo,
      isin,
      mercado,
      emissor_cnpj,
      vencimento,
      serie,
    } = pData;

    return (
      <>
        <Stack flexGrow={1} sx={{ minWidth: "400px" }}>
          <Typography
            variant="caption"
            color="textPrimary"
            sx={{ fontWeight: 600 }}
          >
            {ticker || ativo_seuid || ativo}
          </Typography>
          <Stack direction="row" spacing={{ xs: 0, sm: 1 }} mb={1}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                display: { xs: "none", sm: "inline-block" },
              }}
            >
              {ativo || isin}
            </Typography>
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={1}>
            {mercado && (
              <Chip
                label={ATIVO_MERCADO[mercado].descricao}
                size="small"
                color="primary"
                sx={{ display: { xs: "flex", md: "none" } }}
              />
            )}

            {emissor_cnpj && (
              <Chip
                label={displays.cnpj(emissor_cnpj)}
                size="small"
                color="secondary"
              />
            )}

            {isin && <Chip label={isin} size="small" color="secondary" />}
            {vencimento && (
              <Chip
                label={`Venc.: ${formats.formatDate(vencimento)}`}
                size="small"
                color="secondary"
              />
            )}
            {serie && (
              <Chip label={`Série: ${serie}`} size="small" color="secondary" />
            )}
          </Stack>
        </Stack>
      </>
    );
  };

  const renderAtivos = (pMetadata) => {
    return (
      <Stack>
        <Stack>
          <Typography variant="overline" gutterBottom sx={{ fontWeight: 600 }}>
            Ativos mais aderentes ({pMetadata.ativos.length})
          </Typography>
        </Stack>
        {pMetadata.ativos.map((xAtivo, xIndex) => {
          return (
            <Stack
              id={xAtivo.ativo_id || xIndex}
              key={xAtivo.ativo_id || xIndex}
              direction="row"
              flexGrow={1}
              spacing={3}
              alignItems="flex-start"
              component="li"
              onClick={onClick}
              sx={{
                px: 2,
                py: 2,
                cursor: "pointer",
                "&:hover": {
                  color: "gray",
                  backgroundColor: "background.main",
                },
                borderBottom: "1px solid",
                borderBottomColor: "background.main",
              }}
            >
              <Stack>
                <PessoaImage
                  logoPessoaId={xAtivo.emissor.logo_pessoa_id}
                  alt={xAtivo.emissor.pessoa || xAtivo.ativo}
                  hasLogo={xAtivo.emissor.logo_ok}
                  size={36}
                />
              </Stack>

              {renderAtivo(xAtivo)}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack
      id={benchmark.indicador_id}
      key={benchmark.indicador_id}
      direction="row"
      flexGrow={1}
      spacing={3}
      alignItems="stretch"
      component="li"
      sx={{
        px: 2,
        py: 2,
        cursor: "pointer",
        "&:hover": {
          color: "gray",
          backgroundColor: "background.main",
        },
        borderBottom: "1px solid",
        borderBottomColor: "background.main",
      }}
    >
      {benchmark?.indicador_tipo_id &&
        xIndicadoresTipo[benchmark.indicador_tipo_id] && (
          <Stack
            backgroundColor="primary.main"
            width="36px"
            height="36px"
            justifyContent="center"
            alignItems="center"
            borderRadius="8px"
          >
            <Icon
              iconName={xIndicadoresTipo[benchmark.indicador_tipo_id].icon}
              color="secondary"
              size={18}
            />
          </Stack>
        )}
      <Stack flexGrow={1}>
        <Typography
          variant="body2"
          color="textPrimary"
          sx={{ fontWeight: 600 }}
        >
          {benchmark.indicador_id}
        </Typography>
        <Stack direction="row" spacing={{ xs: 0, sm: 1 }}>
          <Typography
            variant="caption"
            color="textPrimary"
            sx={{
              display: { xs: "none", sm: "inline-block" },
            }}
          >
            {benchmark.indicador}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="center" alignItems="center">
        <Chip
          label={xIndicadoresTipo[benchmark.indicador_tipo_id].label}
          size="small"
          color="secondary"
        />
      </Stack>
      {variant === "default" && (
        <Stack>
          <Stack justifyContent="center" alignItems="center" flexGrow={1}>
            <IconButton onClick={onClick} color="error">
              <Icon iconName="remove" size={21} />
            </IconButton>
          </Stack>
        </Stack>
      )}
      {variant === "select" && (
        <>
          <Stack>
            <Stack justifyContent="center" alignItems="center" flexGrow={1}>
              <Tooltip
                title={renderAtivos(metadata)}
                arrow
                placement="left-start"
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: "none" },
                  },
                }}
              >
                <IconButton color="primary" size="large">
                  <Icon iconName="box_3d" size={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Stack>
            <Stack justifyContent="center" alignItems="center" flexGrow={1}>
              {metadata.cadastrado ? (
                <Checkbox checked={checked} onChange={onChange} />
              ) : (
                <Tooltip title="Recomendação já consta nos seus benchmarks">
                  <IconButton color="warn" size="large">
                    <Icon iconName="ok" size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
});

export default BenchmarkListItem;
